import React, { useEffect, useState } from "react";
import CustomDataTable from "../../components/CustomDataTable";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  requestUsersListAction,
  setResetStateUsersList,
  requestImpersonateUser,
  setResetStateImpersonateUser,
} from "../../Redux/UserState/UserActionCreator";
import Hoc from "../../components/HOC";
import Loader from "../../components/Loader";
import CustomDropdown from "../../components/CustomDropdown";
import { statusFilterOptions } from "../../Constants";

const UserImpersonation = () => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("A");
  const usersListState = useSelector((state) => state.UsersListReducer);
  const impersonateUserState = useSelector(
    (state) => state.ImpersonateUserReducer
  );

  const getStatusParam = (status = statusFilter) =>
    status === "ALL" ? undefined : status;

  const handleStatusFilterChange = (selectedOption) => {
    if (!selectedOption) {
      setStatusFilter("A");
    } else {
      setStatusFilter(selectedOption.value);
    }
  };

  const handleImpersonateUser = (row) => {
    if (!row?.id) return;
    if (row.stsCd !== "A") {
      toast.error("Cannot impersonate a deactivated user");
      return;
    }
    dispatch(requestImpersonateUser(row.id));
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => `${row.firstName || ""} ${row.lastName || ""}`.trim(),
      sortable: true,
      grow: 1.5,
      cell: (row) => (
        <span className="text-md font-medium">
          {row.firstName} {row.lastName}
        </span>
      ),
    },
    {
      name: "Username",
      selector: (row) => row.userName || row.username || "--",
      sortable: true,
      grow: 1.5,
      hide: "sm",
    },
    {
      name: "Email",
      selector: (row) => row.email || "--",
      sortable: true,
      grow: 1.8,
      hide: "sm",
    },
    {
      name: "Contact",
      selector: (row) => row.phone || "--",
      sortable: true,
      grow: 1,
      hide: "sm",
    },
    {
      name: "Role",
      selector: (row) => row.acctType || "--",
      sortable: true,
      grow: 0.8,
      cell: (row) => (
        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border bg-blue-50 text-blue-700 border-blue-200">
          {(row.acctType || "--").toString().replace(/_/g, " ")}
        </span>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.stsCd,
      sortable: true,
      grow: 0.8,
      cell: (row) => {
        const statusText = row.stsCd === "A" ? "Active" : "Deactive";
        const statusStyles =
          row.stsCd === "A"
            ? "bg-green-200 text-green-600"
            : "bg-red-200 text-red-600";

        return (
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${statusStyles}`}
          >
            {statusText}
          </span>
        );
      },
    },
    {
      name: "Actions",
      grow: 0.7,
      cell: (row) => (
        <button
          type="button"
          onClick={() => handleImpersonateUser(row)}
          disabled={impersonateUserState?.loading || row.stsCd !== "A"}
          className="inline-flex items-center px-2 py-1 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          View As
        </button>
      ),
    },
  ];

  const handlePageChange = (page) => {
    dispatch(
      requestUsersListAction({
        page,
        size: usersListState.size,
        search,
        acctType: "USER",
        stsCd: getStatusParam(),
      })
    );
  };

  const handleRowsChange = (size) => {
    dispatch(
      requestUsersListAction({
        page: 0,
        size,
        search,
        acctType: "USER",
        stsCd: getStatusParam(),
      })
    );
  };

  useEffect(() => {
    dispatch(setResetStateUsersList());
    dispatch(
      requestUsersListAction({
        page: 0,
        size: usersListState?.size || 10,
        search: "",
        acctType: "USER",
        stsCd: "A",
      })
    );
    return () => {
      dispatch(setResetStateImpersonateUser());
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(
        requestUsersListAction({
          page: 0,
          size: usersListState?.size,
          search,
          acctType: "USER",
          stsCd: getStatusParam(),
        })
      );
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [search, statusFilter]);

  useEffect(() => {
    if (usersListState?.usersError && !usersListState?.usersLoading) {
      dispatch(setResetStateUsersList());
    }
  }, [usersListState]);

  useEffect(() => {
    if (impersonateUserState?.response && !impersonateUserState?.loading) {
      toast.success("Opened user session in a new tab");
      dispatch(setResetStateImpersonateUser());
    }

    if (impersonateUserState?.error && !impersonateUserState?.loading) {
      toast.error(
        impersonateUserState?.error?.message ||
          impersonateUserState?.error ||
          "Failed to impersonate user"
      );
      dispatch(setResetStateImpersonateUser());
    }
  }, [impersonateUserState, dispatch]);

  return (
    <div className="p-6 w-full h-[90vh]">
      <Loader
        isLoading={
          impersonateUserState?.loading || usersListState?.usersLoading
        }
      />

      <div className="absolute flex items-center mb-4 z-10 right-6">
        <div className="w-[200px] [&>div]:bg-slate-100 [&>div>div]:bg-slate-100 [&>div>div>div]:bg-slate-100 [&>div>label]:bg-slate-100 [&>div>div]:border-b-2 [&>div>div]:border-gray-200">
          <CustomDropdown
            value={statusFilterOptions.find(
              (option) => option.value === statusFilter
            )}
            handleChange={handleStatusFilterChange}
            placeholder="Status"
            options={statusFilterOptions}
          />
        </div>
      </div>

      <CustomDataTable
        columns={columns}
        search={search}
        setSearch={setSearch}
        data={usersListState?.usersResponse?.content}
        enableSearch={true}
        serverPagenation
        paginationTotalRows={usersListState?.usersResponse?.totalElements}
        handleChangePage={handlePageChange}
        handleRowsChange={handleRowsChange}
        fixedHeaderScrollHeight={"65vh"}
      />
    </div>
  );
};

export default Hoc(UserImpersonation);
